import { Theme } from '@material-ui/core/styles'
import useScrollTrigger from '@material-ui/core/useScrollTrigger'
import { createStyles, makeStyles } from '@material-ui/styles'
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { getChatbotToken } from '../../services/chatbot'

const CHATBOT_ENABLED = process.env.REACT_APP_CHATBOT_ENABLED === 'true'

const CHATBOT_BASE_URL = (process.env.REACT_APP_CHATBOT_BASE_URL || '').replace(
  /\/$/,
  '',
)

function getChatbotOrigin(): string {
  if (!CHATBOT_BASE_URL) return ''
  try {
    return new URL(CHATBOT_BASE_URL).origin
  } catch {
    return ''
  }
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    launcher: {
      position: 'fixed',
      right: theme.spacing(1),
      bottom: '5rem',
      zIndex: 1040,
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      padding: 0,
      lineHeight: 0,
      transition: 'bottom 0.2s ease',
    },
    launcherIcon: {
      width: 56,
      height: 56,
      display: 'block',
    },
    launcherIconActive: {
      display: 'none',
    },
    launcherOpen: {
      '& $launcherIcon': { display: 'none' },
      '& $launcherIconActive': { display: 'block' },
    },
    panel: {
      position: 'fixed',
      right: '4rem',
      bottom: '9rem',
      width: 'min(26rem, calc(100vw - 1.5rem))',
      height: 'clamp(22rem, 62vh, 36rem)',
      zIndex: 1040,
      display: 'none',
      flexDirection: 'column',
      overflow: 'hidden',
      borderRadius: 12,
      boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      background: '#fff',
      transition: 'bottom 0.2s ease',
    },
    panelOpen: {
      display: 'flex',
    },
    status: {
      padding: '1rem',
      textAlign: 'center',
      fontSize: '0.9rem',
      color: '#555',
    },
    statusError: {
      color: '#dc2626',
    },
    frameShell: {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    frame: {
      display: 'block',
      width: '100%',
      height: '100%',
      border: 'none',
    },
  }),
)

export default function ChatbotWidget(): ReactElement | null {
  const classes = useStyles()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const isRenewingTokenRef = useRef(false)
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 100 })

  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const fetchToken = useCallback(async (): Promise<string> => {
    const response = await getChatbotToken()
    const payload = response.data
    if (!payload.token) {
      throw new Error(payload.error || 'Falha ao obter token do chatbot.')
    }
    return String(payload.token)
  }, [])

  const loadChatbot = useCallback(async () => {
    if (isLoaded || isLoading || !iframeRef.current) return
    setIsLoading(true)
    setErrorMessage('')
    try {
      const token = await fetchToken()
      iframeRef.current.src = `${CHATBOT_BASE_URL}/embed?token=${encodeURIComponent(
        token,
      )}`
      setIsLoaded(true)
    } catch (err: any) {
      console.warn('Erro ao carregar o chatbot:', err.message)
      setErrorMessage('Não foi possível carregar o assistente.')
    } finally {
      setIsLoading(false)
    }
  }, [isLoaded, isLoading, fetchToken])

  const handleToggle = useCallback(() => {
    const opening = !isOpen
    setIsOpen(opening)
    if (opening) loadChatbot()
  }, [isOpen, loadChatbot])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) setIsOpen(false)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== getChatbotOrigin()) return
      if (!event.data) return

      if (event.data.type === 'close_embed') {
        setIsOpen(false)
        return
      }

      if (event.data.type === 'token_request') {
        if (isRenewingTokenRef.current) return
        isRenewingTokenRef.current = true
        try {
          const renewedToken = await fetchToken()
          if (iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.postMessage(
              { type: 'token_response', token: renewedToken },
              getChatbotOrigin(),
            )
          }
        } catch {
          setErrorMessage('Não foi possível renovar a sessão do assistente.')
        } finally {
          isRenewingTokenRef.current = false
        }
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [fetchToken])

  if (!CHATBOT_ENABLED || !CHATBOT_BASE_URL) return null

  return (
    <>
      <button
        type="button"
        style={{ bottom: scrolled ? '9rem' : '5rem' }}
        className={`${classes.launcher} ${isOpen ? classes.launcherOpen : ''}`}
        onClick={handleToggle}
        aria-controls="chatbot-panel"
        aria-expanded={isOpen}
        aria-label="Assistente virtual"
      >
        <img
          src={`${CHATBOT_BASE_URL}/icon-chat.svg`}
          className={classes.launcherIcon}
          alt=""
          aria-hidden="true"
        />
        <img
          src={`${CHATBOT_BASE_URL}/icon-chat-active.svg`}
          className={`${classes.launcherIcon} ${classes.launcherIconActive}`}
          alt=""
          aria-hidden="true"
        />
      </button>

      <section
        id="chatbot-panel"
        style={{ bottom: scrolled ? '13rem' : '9rem' }}
        className={`${classes.panel} ${isOpen ? classes.panelOpen : ''}`}
        aria-hidden={!isOpen}
      >
        {(isLoading || errorMessage) && (
          <div
            className={`${classes.status} ${
              errorMessage ? classes.statusError : ''
            }`}
          >
            {errorMessage || 'Conectando ao assistente…'}
          </div>
        )}

        {!errorMessage && (
          <div className={classes.frameShell} hidden={!isLoaded}>
            <iframe
              ref={iframeRef}
              id="chatbot-frame"
              className={classes.frame}
              title="Assistente virtual de serviços"
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              sandbox="allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox"
              allow="clipboard-write; microphone"
            />
          </div>
        )}
      </section>
    </>
  )
}
